/**
 * v2 API contract of «Клиенты» (/clients, /clients/:id): the list, the card's tabs and the inline
 * field edits (docs/research/design2-records.md §2, §8 R6/R8; decisions-log V32).
 */
import { z } from 'zod';
import { CURRENCIES } from '../money';
import type { RateLike } from '../ru';

export interface ClientListRow {
  readonly id: string;
  readonly name: string;
  /** In-house short name («кодовое название»); null = none. */
  readonly codeName: string | null;
  /** Projects that link the client (main, extra or payer), any status (V134). */
  readonly projectCount: number;
  /**
   * «Оборот, USD» (V134, V135): everything received from the client, all time, each payment in USD at
   * the rate of its accrual — the date of the invoice it pays (the payment date without one).
   */
  readonly turnoverUsdMinor: number;
  /** Payments left out of the turnover for want of a rate (none within 10 days of the date). */
  readonly turnoverUnconverted: number;
  /** Some active project links the client (tab «Активные»). */
  readonly isActive: boolean;
}

export interface ClientsListResponse {
  readonly items: readonly ClientListRow[];
}

export type ClientProjectStatus = 'active' | 'paused' | 'ended';

export interface ClientEntityLink {
  readonly id: string;
  /** «{name} · {country}». */
  readonly label: string;
}

/** A project that links the client (main client, extra client or payer). */
export interface ClientProjectItem {
  readonly id: string;
  readonly name: string;
  readonly status: ClientProjectStatus;
  readonly otherClientNames: readonly string[];
  /** Price in force this month; null → «нет ставки». */
  readonly rate: RateLike | null;
  /** The contract of this client's link to the project. */
  readonly contractId: string | null;
}

export interface ClientContractOption {
  readonly id: string;
  readonly number: string;
  readonly ended: boolean;
}

export type ClientInvoiceDaysTone = 'late' | 'onTime' | 'overdue' | 'waiting';

/** «Счета и оплаты» row (R6). */
export interface ClientInvoiceRow {
  readonly id: string;
  /** Our or the client's number; null for a payroll statement («Ведомость»). */
  readonly number: string | null;
  readonly projectNames: string;
  readonly period: string;
  readonly amountMinor: number;
  readonly currency: string;
  readonly status: string;
  readonly sentOn: string | null;
  readonly dueOn: string | null;
  readonly paidOn: string | null;
  readonly days: number | null;
  readonly daysTone: ClientInvoiceDaysTone | null;
  readonly daysTitle: string | null;
}

export type ClientEventTarget =
  | { readonly kind: 'contract'; readonly id: string }
  | { readonly kind: 'project'; readonly id: string }
  | { readonly kind: 'invoices'; readonly period: string };

/** «Прочие события» row. */
export interface ClientEvent {
  readonly date: string;
  readonly title: string;
  readonly sub: string;
  readonly target: ClientEventTarget;
}

export interface ClientCard {
  readonly id: string;
  readonly name: string;
  readonly codeName: string | null;
  readonly country: string | null;
  readonly currency: string;
  readonly paymentTermsDays: number | null;
  readonly emails: readonly string[];
  readonly portal: string | null;
  /** «Наше юрлицо» (R8): entities of the active contracts, else the inferred one. */
  readonly entities: readonly ClientEntityLink[];
  readonly contractCount: number;
  readonly contracts: readonly ClientContractOption[];
  readonly projects: readonly ClientProjectItem[];
  readonly invoices: readonly ClientInvoiceRow[];
  readonly events: readonly ClientEvent[];
}

/** Longest payment term accepted by the inline field. */
export const CLIENT_MAX_TERMS_DAYS = 365;

const optionalText = z.string().trim().max(500);

/** PATCH /clients/:id — one field at a time (inline edits commit on blur). */
export const clientFieldUpdateSchema = z.union([
  z.object({ name: z.string().trim().min(1, 'Укажите название').max(300) }),
  z.object({ codeName: z.string().trim().max(100) }),
  z.object({ country: optionalText }),
  z.object({ currency: z.enum(CURRENCIES) }),
  z.object({ paymentTermsDays: z.number().int().min(0).max(CLIENT_MAX_TERMS_DAYS).nullable() }),
  z.object({ emails: z.array(z.string().trim().email('Проверьте адрес')).max(20) }),
  z.object({ portal: optionalText }),
]);
export type ClientFieldUpdate = z.infer<typeof clientFieldUpdateSchema>;

/** PUT /clients/:id/projects/:projectId/contract */
export const clientProjectContractSchema = z.object({ contractId: z.string().min(1).nullable() });
export type ClientProjectContractInput = z.infer<typeof clientProjectContractSchema>;

export interface ClientWriteResult {
  readonly message: string;
  /** Null when nothing changed. */
  readonly undoToken: string | null;
  /** POST /clients (V170): the new client's id, its card opens next. */
  readonly id?: string;
}
